import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { getUsers, getUserDetails } from 'api/user/users';
import { getUsersByRoleAndName } from 'api/user/user-filter';

import { SCREENS_KEYS } from 'constants/constants';

import { useScreen } from 'hooks';

import type { Pagination } from 'commons/modules/pagination';
import type {
  DataAccordionType,
  LegalRepresentativeValues,
  OpenType,
} from './types';

import LegalRepresentativesManagement from './context';

interface Props {
  children: React.ReactNode;
}

const LegalRepresentativesProvider = ({ children }: Props) => {
  const { onlyQuery } = useScreen(SCREENS_KEYS.configurationUsers);

  // From table context
  const [error, setError] = useState<boolean | string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 50,
    total: 0,
    isLastPage: false,
  });
  const [dataAccordion, setDataAccordion] = useState<DataAccordionType>({
    columns: [],
    data: [],
    rows: [],
    pagination,
  });
  // End from table context

  const [open, setOpen] = useState<OpenType>({
    isOpen: false,
    clientId: null,
    step: 0,
  });
  const [matchClients, setMatchClients] = useState<string[]>([]);
  const [matchLegalRepresentatives, setMatchLegalRepresentatives] = useState<
    LegalRepresentativeValues[]
  >([]);
  const [queryParams, setQueryParams] = useState<Record<string, string>>({
    email: '',
    role: '',
  });

  const colspan = 1;
  const pageNum = pagination.page - 1;

  const onChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    value: number,
  ) => {
    const totalPages = Math.ceil((pagination.total || 0) / pagination.pageSize);
    const newPage = value + 1;
    const isLastPage = newPage === totalPages;
    setPagination({
      ...dataAccordion.pagination,
      page: newPage,
      pageSize: pagination.pageSize,
      isLastPage,
    });
  };

  const onChangePageSize = (event: React.ChangeEvent<any>) => {
    setPagination({
      page: 1,
      pageSize: event.target.value,
      total: pagination.total,
      isLastPage: false,
    });
  };

  const handleService = async (
    currentService: any,
    params: Record<string, any>,
  ) => {
    if (currentService) {
      setError(false);
      setLoaded(false);
      setLoading(true);
      const { data, success, errorMessage } = await currentService(
        params,
        pagination,
      );

      if (success) {
        setDataAccordion(data);
        setLoaded(true);
        setLoading(false);
      } else {
        setError(errorMessage);
        setLoaded(false);
        setLoading(false);
      }
    }
  };

  const getLegalRepresentativeFilter = async (name: string) => {
    if (name) {
      const { data, success } = await getUsersByRoleAndName({ names: name });
      if (success && data && data.data) {
        const clients = data.data.map((client) => {
          const {
            name: clientName,
            fathersLastName,
            mothersLastName,
            amibRegistration,
            email,
          } = client;
          return {
            fullName: _.startCase(
              _.toLower(
                `${clientName} ${fathersLastName} ${mothersLastName || ''}`,
              ),
            ),
            name: _.startCase(_.toLower(clientName)),
            fathersLastName: _.startCase(_.toLower(fathersLastName)),
            mothersLastName: _.startCase(_.toLower(mothersLastName)),
            amibRegistration,
            email,
          };
        });
        setMatchLegalRepresentatives(clients);
        return success;
      }
    }
    setMatchClients([]);
    setMatchLegalRepresentatives([]);
    return false;
  };

  const getLegalRepresentativeDetails = (amib: string) => getUserDetails(amib);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      handleService(getUsers, queryParams);
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, queryParams]);

  return (
    <LegalRepresentativesManagement.Provider
      value={{
        loading,
        loaded,
        error,
        onlyQuery,
        pagination,
        colspan,
        pageNum,
        dataAccordion,
        matchClients,
        matchLegalRepresentatives,
        open,
        setOpen,
        setQueryParams,
        setPagination,
        setMatchClients,
        setMatchLegalRepresentatives,
        onChangePage,
        onChangePageSize,
        handleService,
        getLegalRepresentativeFilter,
        getLegalRepresentativeDetails,
        setDataAccordion,
      }}
    >
      {children}
    </LegalRepresentativesManagement.Provider>
  );
};

export default LegalRepresentativesProvider;
